<template>
	<v-container>
		<v-row class="row profile-main-container mx-auto">
			<v-col class="col-md-6 d-flex flex-row-reverse">
				<v-avatar
					:size="130"
					tile
					class="rounded-lg mr-4 user-avatar"
					:color="theme.accent"
				>
					<template v-if="user.image">
						<v-img :src="user.image" :alt="`[${user.name}]`" />
					</template>
					<template v-else>
						<span class="white--text headline">{{ getInitials() }}</span>
					</template>
				</v-avatar>
			</v-col>
			<v-col class="col-md-6">
				<!--headline - alt for display-2-->
				<h5
					class="display-1 font-weight-bold mb-3 mt-0"
					:class="theme.dark && 'col-white'"
				>
					{{ user.name }}
				</h5>
				<div
					class="font-weight-light"
					:class="[theme.dark ? 'col-white' : 'text--disabled']"
				>
					@{{ user.username }}
				</div>
				<v-btn
					v-if="editing == false"
					class="mt-4 text-capitalize border rounded-lg"
					@click="editing = true"
					:class="theme.dark && 'col-white'"
					width="150"
					depressed
					text
				>
					Edit profile
					<v-icon :color="'grey lighten-1'" class="ml-2">mdi-pencil</v-icon>
				</v-btn>
				<template v-else>
					<v-btn
						class="white--text mt-4 text-capitalize rounded-lg font-small"
						:color="theme.primary"
						@click="saveEdits"
						width="100"
						depressed
						:disabled="can_submit == false"
					>
						Save
						<v-icon :color="'grey lighten-1'" class="ml-2">mdi-check</v-icon>
					</v-btn>
					<v-btn
						class="mt-4 ml-2 text-capitalize border rounded-lg"
						@click="editing = false"
						width="50"
						depressed
						icon
					>
						<v-icon :color="'grey lighten-1'">mdi-lock</v-icon>
					</v-btn>
				</template>
			</v-col>
			<div class="mx-auto pt-5">
				<v-row>
					<v-col v-for="star in stars" :key="star.id">
						<div class="d-inline-flex" style="width: 100px; ">
							<div style="width: 70px">
								<v-sparkline
									class="mt-3"
									width="70"
									height="6"
									:value="[0, 0, 0]"
									:color="
										user.level.level >= star.cut_off ||
										user.level.name == star.name
											? star.color
											: 'grey lighten-2'
									"
									smooth
									:padding="5"
									:line-width="2"
									stroke-linecap="round"
									gradient-direction="right"
									type="trend"
									auto-draw
								></v-sparkline>
							</div>
							<v-icon
								:color="star.color"
								:size="18"
								class="mt-auto mb-auto pt-1"
								:class="
									user.level.name == star.name &&
										'border rounded-circle pa-1 pt-0'
								"
								>star</v-icon
							>
						</div>
						<v-hover class="mx-2 my-2 cursor-default">
							<template v-slot="{ hover }">
								<div
									:class="`elevation-${hover ? 14 : 1}`"
									class="transition-swing rounded-lg"
								>
									<div
										v-if="user.level.name == star.name"
										class="caption text-center rounded-lg pa-1 grey-bg col-white"
										:style="{ 'background-color': theme.primary }"
									>
										{{ user.level.name }}
									</div>
								</div>
							</template>
						</v-hover>
					</v-col>
				</v-row>
			</div>
		</v-row>
		<v-card class="mt-5 pa-10 profile-inner-container mx-auto">
			<div class="inner-content">
				<v-row class="mb-2">
					<v-col class="col-md-2 col-sm-3 font-under-normal">
						Email
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						{{ user.email }}
					</v-col>
				</v-row>
				<v-row class="mb-n3" :class="editing == false && 'mb-2'" v-if="editing">
					<v-col
						class="col-md-2 col-sm-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						Name
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						<v-text-field
							label="Full name"
							placeholder="Full name"
							v-model="form.name"
							solo
						></v-text-field>
					</v-col>
				</v-row>
				<v-row class="mb-n3" :class="editing == false && 'mb-2'" v-if="editing">
					<v-col
						class="col-md-2 col-sm-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						Username
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						<v-text-field
							label="Username"
							placeholder="Username"
							v-model="form.username"
							solo
						></v-text-field>
					</v-col>
				</v-row>
				<v-row class="mb-2">
					<v-col class="col-md-2 col-sm-3 font-under-normal">
						<!-- <v-icon :color="'grey lighten-1'">mdi-pencil</v-icon> -->
						Star level
					</v-col>
					<v-col class="col-md-10 col-9">
						{{ user.level.name }} • {{ user.level.level }}
						<small class="ml-1"
							>Rank up with {{ user.level.cut_off - user.level.level + 1 }} more
							stars.</small
						>
					</v-col>
				</v-row>
				<v-row class="mb-2" v-if="editing">
					<v-col
						class="col-md-2 col-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						<!-- <v-icon :color="'grey lighten-1'">mdi-pencil</v-icon> -->
						Picture
					</v-col>
					<v-col class="col-md-10 col-9">
						<div class="row">
							<v-col class="col-md-8">
								<v-file-input
									:rules="[
										value =>
											!value ||
											value.size < 500000 ||
											'Image size should be less than 500KB',
									]"
									accept="image/*"
									v-if="editing"
									show-size
									placeholder="Set profile picture"
									v-model="form.image"
									solo
									:disabled="form.delete_image"
								></v-file-input>
							</v-col>
							<v-col class="col-md-4">
								<v-checkbox
									v-if="user.image"
									v-model="form.delete_image"
									label="Delete profile picture"
								></v-checkbox>
							</v-col>
						</div>
					</v-col>
				</v-row>

				<v-row class="mb-2">
					<v-col
						class="col-md-2 col-sm-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						<!-- <v-icon :color="'grey lighten-1'">mdi-pencil</v-icon> -->
						Grade
					</v-col>
					<v-col class="col-md-10 col-9">
						<template v-if="editing">
							<v-select
								:cols="4"
								:items="Array.from(Array(8).keys()).map(i => i + 5)"
								label="Grade"
								placeholder="Grade"
								v-model="form.grade"
								solo
							></v-select>
						</template>
						<template v-else>
							{{ user.grade }}
						</template>
					</v-col>
				</v-row>
				<v-row class="mb-2">
					<v-col class="col-md-2 col-sm-3 font-under-normal">
						<!-- <v-icon :color="'grey lighten-1'">mdi-pencil</v-icon> -->
						School
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						{{ user.school.name }}
					</v-col>
				</v-row>
				<v-row class="mb-2">
					<v-col class="col-md-2 col-sm-3 font-under-normal">
						<!-- <v-icon :color="'grey lighten-1'">mdi-pencil</v-icon> -->
						Joined
					</v-col>
					<v-col class="col-md-10 col-9">
						{{ formatDate(user.created_at) }}
					</v-col>
				</v-row>
				<v-row class="mt-10 ml-0" v-if="editing">
					<div class="caption">
						Leave password fields blank to retain current password.
					</div>
				</v-row>
				<v-row
					class="mb-n3 mt-5"
					:class="editing == false && 'mb-2'"
					v-if="editing"
				>
					<v-col
						class="col-md-2 col-sm-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						Password
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						<v-text-field
							name="password"
							v-model="form.password"
							label="Current password"
							placeholder="Current password"
							:append-icon="show_password ? 'visibility' : 'visibility_off'"
							@click:append="() => (show_password = !show_password)"
							:type="show_password ? 'password' : 'text'"
							solo
						></v-text-field>
					</v-col>
				</v-row>
				<v-row class="mb-n3" :class="editing == false && 'mb-2'" v-if="editing">
					<v-col
						class="col-md-2 col-sm-3 font-under-normal"
						:class="editing && 'editing-padding'"
					>
						New Password
					</v-col>
					<v-col class="col-md-10 col-9 text--disabled">
						<v-row>
							<v-col cols="12" class="col-md-6">
								<v-text-field
									name="password"
									v-model="form.new_password"
									label="Password"
									placeholder="Password"
									:append-icon="
										show_new_password ? 'visibility' : 'visibility_off'
									"
									@click:append="() => (show_new_password = !show_new_password)"
									:type="show_new_password ? 'password' : 'text'"
									solo
								></v-text-field>
							</v-col>
							<v-col>
								<v-text-field
									name="password"
									v-model="form.confirm_new_password"
									label="Confirm Password"
									placeholder="Confirm Password"
									:append-icon="
										show_confirm_new_password ? 'visibility' : 'visibility_off'
									"
									@click:append="
										() =>
											(show_confirm_new_password = !show_confirm_new_password)
									"
									:rules="[
										v =>
											v == this.form.new_password || 'Passwords don\'t match',
									]"
									:type="show_confirm_new_password ? 'password' : 'text'"
									solo
								></v-text-field>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
				<template>
					<div class="text-center">
						<v-dialog v-model="dialog" width="500">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									color="red"
									dark
									v-bind="attrs"
									v-on="on"
									text
									class="text-capitalize mt-5"
								>
									Delete my account
									<v-icon size="20" class="ml-2">mdi-alert</v-icon>
								</v-btn>
							</template>

							<v-card>
								<v-card-title>
									Are you sure?
								</v-card-title>

								<v-card-text>
									This will permanently delete your simplelearn account,
									alongside your star progress. Please enter your password to
									confirm
								</v-card-text>

								<div class="mx-6 my-2">
									<v-text-field
										name="password"
										v-model="password"
										label="Enter your password"
										placeholder="Enter your password"
										:append-icon="
											show_password_delete ? 'visibility' : 'visibility_off'
										"
										@click:append="
											() => (show_password_delete = !show_password_delete)
										"
										:type="show_password_delete ? 'password' : 'text'"
										@keydown.enter="deleteAccount"
										solo
									></v-text-field>
								</div>

								<v-divider></v-divider>

								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn
										class="text-capitalize"
										:color="theme.primary"
										text
										@click="dialog = false"
									>
										Cancel
									</v-btn>
									<v-btn
										class="text-capitalize"
										color="red"
										text
										@click="deleteAccount"
									>
										Yes, delete
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</div>
				</template>
			</div>
		</v-card>
	</v-container>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
export default {
	name: 'Profile',

	data: () => ({
		stars: [],
		editing: false,
		form: {},
		show_new_password: false,
		show_confirm_new_password: false,
		show_password: false,
		show_password_delete: false,
		password: null,
		dialog: false,
	}),

	methods: {
		...mapMutations(['setAlert', 'removeAlert', 'setUser', 'logout']),
		getInitials() {
			return this.user.name
				.split(' ')
				.map(w => w[0])
				.join('')
				.toUpperCase()
		},
		deleteAccount() {
			this.dialog = false
			this.core.http
				.delete(`auth/user`, { password: this.password })
				.then(response => {
					if (response.data.success == true) {
						this.logout()
					}
				})
				.catch(error => {
					this.setAlert(error.response)
					this.password = null
				})
		},
		formatDate(date) {
			return this.core.moment(date).format('ll')
		},
		saveEdits() {
			const data = new FormData()

			// Delete unset values
			for (var i in this.form) {
				if (this.form[i] == '' || this.form[i] == null) delete this.form[i]
			}

			data.append('_method', 'PATCH')
			Object.keys(this.form).forEach(key => {
				const value = this.form[key]
				data.append(key, value)
			})
			if (this.form.image) data.append('image', this.form.image)

			this.core.http
				.post('auth/user', data)
				.then(response => {
					this.setAlert(response)
					this.setUser(response.data.user)
					this.editing = false
					this.prefillDetails()
				})
				.catch(error => {
					this.setAlert(error.response)
				})
		},
		prefillDetails() {
			this.form = {
				name: this.user.name,
				username: this.user.username,
				image: null,
				grade: parseInt(this.user.grade),
				password: '',
				new_password: '',
				confirm_new_password: '',
				delete_image: false,
			}
		},
	},

	mounted() {
		this.core.http
			.get(`stars`)
			.then(response => {
				this.stars = response.data.stars.sort((a, b) => a.cut_off - b.cut_off)
				this.prefillDetails()
			})
			.catch(error => {
				this.setAlert(error.response)
			})
	},

	computed: {
		...mapGetters(['theme', 'user', 'loading']),
		can_submit: function() {
			const f = this.form
			return (
				f.name != '' &&
				f.username != '' &&
				f.grade != null &&
				((f.password != '' &&
					f.new_password != '' &&
					f.confirm_new_password != '' &&
					f.confirm_new_password == f.new_password) ||
					(f.password == '' &&
						f.new_password == '' &&
						f.confirm_new_password == ''))
			)
		},
	},
}
</script>

<style scoped>
.profile-main-container {
	max-width: 800px;
}
.user.name {
}
.grey-bg {
	background-color: lightgrey;
}
.profile-inner-container {
	max-width: 800px;
}
.inner-content {
	/* max-width: 400px; */
}
.editing-padding {
	padding-top: 3.5%;
}
@media only screen and (max-width: 400px) {
	.user-avatar {
		min-width: 110px !important;
		width: 110px !important;
	}
}
</style>
